import { isPlatformBrowser } from "@angular/common";
import {
  Component,
  Inject,
  OnDestroy,
  OnInit,
  PLATFORM_ID,
} from "@angular/core";
import { DomSanitizer, Meta, SafeHtml } from "@angular/platform-browser";
import { ActivatedRoute } from "@angular/router";
import { TranslocoService } from "@ngneat/transloco";
import { GeneralService } from "app/services/general.service";
import { HreflangService } from "app/services/hreflang.service";
import { Equipment } from "app/shared/equipment";
import { Machine } from "app/shared/machine";
import { Observable, of, Subscription } from "rxjs";
import { catchError, map, shareReplay, switchMap, tap } from "rxjs/operators";

@Component({
  selector: "app-machines",
  templateUrl: "./machines.component.html",
  styleUrls: ["./machines.component.scss"],
})
export class MachinesComponent implements OnInit, OnDestroy {
  machines$!: Observable<Machine[]>;
  equipment!: Equipment | undefined;

  activeLang: string = "EN";
  firstRun = false;
  private langChangesSubscription!: Subscription;

  errorMsg: string | null = null;

  constructor(
    public router: ActivatedRoute,
    @Inject(PLATFORM_ID) private platformId: object,
    private _translocoService: TranslocoService,
    private meta: Meta,
    private hreflangService: HreflangService,
    private sanitizer: DomSanitizer,
    private generalService: GeneralService
  ) {}

  ngOnInit(): void {
    this.getActiveLang();

    // ✅ خذ الـ id من URL ثم اجلب البيانات
    this.machines$ = this.router.params.pipe(
      map((p) => String(p["id"] ?? "")),
      tap((id) => {
        // تحديث hreflang بعد تغيّر المسار
        if (!this.firstRun) {
          setTimeout(
            () =>
              this.hreflangService.updateHreflangTags(
                this.activeLang,
                `machines/${id}`
              ),
            1000
          );
          this.firstRun = true;
        }
      }),
      switchMap((id) =>
        this.generalService.getItems(`machine/byCate/${id}`).pipe(
          // متوقع يرجع شكل: { machines: Machine[], cate: Equipment }
          tap((payload: any) => {
            this.equipment = payload?.cate;
            // خزّن عيّنة عشوائية (اختياري) — على المتصفح فقط
            if (isPlatformBrowser(this.platformId)) {
              const arr: Machine[] = payload?.machines ?? [];
              const sample = this.pickSample(arr);
              try {
                localStorage.setItem(
                  "selectedMachines",
                  JSON.stringify(sample)
                );
              } catch {}
            }
          }),
          map((payload: any) => {
            const machines: Machine[] = payload?.machines ?? [];
            const ids: string[] = payload?.cate?.sortedMachines ?? [];
            return this.sortMachinesByIds(machines, ids);
          })
        )
      ),
      catchError((err) => {
        this.errorMsg = (err && (err.message || err.statusText)) || "Error";
        return of([] as Machine[]);
      }),
      shareReplay(1)
    );
  }

  private sortMachinesByIds(machines: Machine[], ids: string[]): Machine[] {
    if (!ids?.length) return machines ?? [];
    const order = new Map<string, number>();
    ids.forEach((id, i) => order.set(id, i));
    return [...(machines ?? [])].sort(
      (a, b) => (order.get(a._id) ?? 1e9) - (order.get(b._id) ?? 1e9)
    );
  }

  private pickSample(list: Machine[]): Machine[] {
    const len = list.length;
    if (len <= 1) return list.slice(0, 1);
    if (len === 3) return this.randomSlice(list, 2);
    if (len === 4) return this.randomSlice(list, 3);
    if (len > 5) return this.randomSlice(list, 5);
    return this.randomSlice(list, Math.max(1, len));
  }

  private randomSlice<T>(arr: T[], size: number): T[] {
    const shuffled = [...arr].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, size);
  }

  getActiveLang() {
    this.langChangesSubscription =
      this._translocoService.langChanges$.subscribe((activeLang) => {
        if (this.activeLang !== activeLang) {
          this.deleteSchemaMeta();
          const id = this.router.snapshot.params["id"];
          setTimeout(
            () =>
              this.hreflangService.updateHreflangTags(
                activeLang,
                `machines/${id}`
              ),
            1000
          );
        }
        this.activeLang = activeLang;
      });
  }

  getSafeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  ngOnDestroy() {
    if (this.langChangesSubscription)
      this.langChangesSubscription.unsubscribe();

    // Delete Robots
    const robotTags = this.meta.getTags('name="robots"');
    if (robotTags.length > 0) {
      robotTags.forEach((tag) => {
        try {
          this.meta.removeTagElement(tag);
        } catch (error) {
          console.error("Error removing robot tag:", error);
        }
      });
    }

    this.deleteSchemaMeta();
  }

  deleteSchemaMeta() {
    // Delete Hreflang
    const hreflangTags = this.meta.getTags('name="hreflang"');
    if (hreflangTags.length > 0) {
      hreflangTags.forEach((tag) => {
        try {
          this.meta.removeTagElement(tag);
        } catch (error) {
          console.error("Error removing hreflang tag:", error);
        }
      });
    }
    // Delete Canonical
    try {
      this.meta.removeTag("rel=canonical");
    } catch (error) {
      console.error("Error removing canonical tag:", error);
    }
  }

  trackById = (_: number, it: Machine) => it._id;
}
